import React, { useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Textarea, Select } from "../components/UI";
import { Wand2, Loader2, Download, RefreshCcw, Sparkles } from "lucide-react";

const TONE = ["Bold", "Trustworthy", "Urgent", "Inspirational", "Presidential", "Grassroots"];
const PRESETS = [
  { label: "CTV Endframe — GOTV", prompt: "Connected TV ad endframe, 16:9 cinematic. 'Vote' theme, American flag motif softly blurred in background, deep navy and warm gold light. Clean space for candidate name and disclaimer strip at bottom. No specific candidate. Broadcast quality." },
  { label: "CTV Storyboard — Issue Ad", prompt: "Cinematic CTV storyboard frame, 16:9. Main street of an American town at golden hour, families, local businesses. Documentary realism, hopeful tone. Space for message overlay. Broadcast quality." },
  { label: "Fundraising Appeal", prompt: "Political fundraising banner. Dramatic navy background with a rising red-white-blue light streak, 'match deadline' urgency feel. Bold headline safe area and donate button space. No candidate likeness." },
  { label: "Voter Education", prompt: "Neutral, professional political campaign banner. American flag motif subtle in background. Deep navy and white. Space for disclaimer at bottom. No specific candidate. 1024x1024." },
  { label: "Debate Night Promo", prompt: "CTV promo frame, 16:9: empty debate stage with two podiums under dramatic spotlights, navy and cyan lighting, broadcast news aesthetic, headline safe area top third." },
];

export default function AICreativeStudio() {
  const [prompt, setPrompt] = useState(PRESETS[0].prompt);
  const [name, setName] = useState("");
  const [tone, setTone] = useState("Premium");
  const [audience, setAudience] = useState("");
  const [brand, setBrand] = useState("");
  const [offer, setOffer] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [refineLog, setRefineLog] = useState([]);
  const [refineText, setRefineText] = useState("");
  const [refining, setRefining] = useState(false);

  const buildPrompt = () => {
    const parts = [prompt];
    if (tone) parts.push(`Tone: ${tone}.`);
    if (audience) parts.push(`Target audience: ${audience}.`);
    if (brand) parts.push(`Brand: ${brand}.`);
    if (offer) parts.push(`Offer/message: ${offer}.`);
    parts.push("Leave clear safe area for headline text overlay. High-end commercial advertising photography quality.");
    return parts.join(" ");
  };

  const generate = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/ai/image", {
        prompt: buildPrompt(),
        name: name || undefined,
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const refineImage = async () => {
    const text = refineText.trim();
    if (!text || refining) return;
    setRefining(true); setError("");
    setRefineLog((l) => [...l, { role: "user", text }]);
    setRefineText("");
    try {
      const { data } = await api.post("/ai/image", {
        prompt: `${buildPrompt()} Revision instruction: ${text}.`,
        name: name || undefined,
      });
      setResult(data);
      setRefineLog((l) => [...l, { role: "assistant", text: "Updated the creative with your change." }]);
    } catch (e) {
      setRefineLog((l) => [...l, { role: "assistant", text: "Couldn't apply that — try rephrasing." }]);
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setRefining(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Powered by Gemini Nano Banana"
        title="AI Creative Studio"
        subtitle="Generate banner ad creatives from natural-language prompts. All output is logged and requires human approval before launch."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Brief</div>
          <div className="space-y-3">
            <Input label="Creative name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Spring Bistro - Lunch" data-testid="studio-name" />
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1.5">Quick presets</div>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map(p => (
                  <button key={p.label} onClick={() => setPrompt(p.prompt)} data-testid={`preset-${p.label}`}
                    className="px-2.5 py-1 rounded-full text-[11px] bg-white/5 border border-white/10 text-slate-300 hover:bg-cyan-400/10 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors">
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <Textarea label="Prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="studio-prompt" />
            <Select label="Tone" value={tone} onChange={(e) => setTone(e.target.value)}>
              {TONE.map(t => <option key={t}>{t}</option>)}
            </Select>
            <Input label="Audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Suburban swing voters, GA-06" />
            <Input label="Campaign / entity" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Voss for State Senate" />
            <Input label="Message" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Early voting starts Oct 14" />
            <Btn onClick={generate} disabled={loading || !prompt.trim()} className="w-full justify-center" data-testid="generate-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {loading ? "Generating…" : "Generate banner"}
            </Btn>
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Preview</div>
          <div className="aspect-video bg-slate-900/60 rounded-md border border-white/10 flex items-center justify-center overflow-hidden" data-testid="preview-area">
            {loading && (
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
                <div className="mt-3 text-sm text-slate-400">Composing your creative…</div>
                <div className="mt-1 text-xs text-slate-500">This usually takes 8–20 seconds</div>
              </div>
            )}
            {!loading && !result && !error && (
              <div className="text-center">
                <Sparkles className="w-10 h-10 text-slate-700 mx-auto" />
                <div className="mt-3 text-sm text-slate-500">Your banner will appear here</div>
              </div>
            )}
            {error && <div className="text-rose-300 text-sm p-4 text-center">{error}</div>}
            {result && (
              <img src={result.image_base64} alt="Generated" className="w-full h-full object-contain" data-testid="generated-image" />
            )}
          </div>
          {result && (
            <div className="mt-4 flex items-center gap-2">
              <Btn variant="secondary" onClick={generate}><RefreshCcw className="w-4 h-4" /> Regenerate</Btn>
              <a
                href={result.image_base64}
                download={`totalreach-creative.png`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white/5 border border-white/10 text-white hover:bg-white/10"
                data-testid="download-creative-link"
              >
                <Download className="w-4 h-4" /> Download
              </a>
              <div className="ml-auto text-xs text-slate-500">Saved to Creatives Library · awaiting approval</div>
            </div>
          )}
          {result && (
            <div className="mt-4 border-t border-white/10 pt-4" data-testid="refine-chat">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-cyan-300" /> Chat to improve this ad
              </div>
              {refineLog.length > 0 && (
                <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto" data-testid="refine-thread">
                  {refineLog.map((m, i) => (
                    <div key={i} className={`text-sm px-3 py-1.5 rounded-lg max-w-[85%] ${m.role === "user" ? "ml-auto bg-cyan-500/20 text-cyan-100" : "bg-white/5 text-slate-300"}`}>
                      {m.text}
                    </div>
                  ))}
                  {refining && <div className="text-sm text-cyan-300 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying…</div>}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input value={refineText} onChange={(e) => setRefineText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !refining) refineImage(); }}
                  placeholder="e.g. brighter, add a bold headline space, warmer tone"
                  data-testid="refine-input" disabled={refining}
                  className="flex-1 px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/40" />
                <Btn onClick={refineImage} disabled={refining || !refineText.trim()} data-testid="refine-send">
                  {refining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Refine
                </Btn>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
